import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Users, Plus, Pencil, Trash2, Shield, Eye, EyeOff,
  CheckCircle2, XCircle, Search, ChevronDown, ChevronUp,
  Loader2, Mail, Lock, User, AlertCircle, Key, Copy, RotateCcw, Check
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PERMISSION_CATEGORIES = [
  { id: 'videos', label: 'Videos', group: 'Content' },
  { id: 'series', label: 'Series', group: 'Content' },
  { id: 'reels', label: 'Reels', group: 'Content' },
  { id: 'ad-reels', label: 'Ad Reels', group: 'Content' },
  { id: 'categories', label: 'Categories', group: 'Content' },
  { id: 'blog', label: 'Blog Management', group: 'Content' },
  { id: 'comments', label: 'Comments', group: 'Content' },
  { id: 'homepage', label: 'Homepage Layout', group: 'Content' },
  { id: 'purchases', label: 'Purchases', group: 'Commerce' },
  { id: 'revenue', label: 'Revenue', group: 'Commerce' },
  { id: 'membership', label: 'Membership', group: 'Commerce' },
  { id: 'subscribers', label: 'Subscribers', group: 'Commerce' },
  { id: 'subscriptions', label: 'Subscriptions', group: 'Commerce' },
  { id: 'stripe-settings', label: 'Stripe Settings', group: 'Commerce' },
  { id: 'analytics', label: 'Analytics', group: 'Insights' },
  { id: 'reels-analytics', label: 'Reel Analytics', group: 'Insights' },
  { id: 'seo', label: 'SEO', group: 'Insights' },
  { id: 'ads', label: 'Ad Management', group: 'Management' },
  { id: 'banner-ads', label: 'Banner Ads', group: 'Management' },
  { id: 'popup', label: 'Pop-ups', group: 'Management' },
  { id: 'creators', label: 'Curators', group: 'Management' },
  { id: 'creator-accounts', label: 'Creator Accounts', group: 'Management' },
  { id: 'users', label: 'Users', group: 'Management' },
];

const PERMISSION_LEVELS = [
  { value: 'none', label: 'No Access', color: 'bg-gray-600 text-gray-300' },
  { value: 'view', label: 'View Only', color: 'bg-blue-600 text-blue-100' },
  { value: 'edit', label: 'Edit', color: 'bg-amber-600 text-amber-100' },
  { value: 'full', label: 'Full Access', color: 'bg-green-600 text-green-100' },
];

const PermissionBadge = ({ level }) => {
  const config = PERMISSION_LEVELS.find(l => l.value === level) || PERMISSION_LEVELS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

const PermissionSelector = ({ value, onChange, disabled }) => {
  return (
    <div className="flex gap-1">
      {PERMISSION_LEVELS.map(level => (
        <button
          key={level.value}
          onClick={() => onChange(level.value)}
          disabled={disabled}
          className={`px-2.5 py-1 text-xs rounded-md font-medium transition-all ${
            value === level.value
              ? level.value === 'none' ? 'bg-gray-600 text-white ring-2 ring-gray-400'
                : level.value === 'view' ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                : level.value === 'edit' ? 'bg-amber-600 text-white ring-2 ring-amber-400'
                : 'bg-green-600 text-white ring-2 ring-green-400'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          {level.label}
        </button>
      ))}
    </div>
  );
};

const StaffManagement = ({ authToken }) => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState({});

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', permissions: {}
  });

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/staff`, { headers });
      setStaffList(res.data.staff || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', permissions: {} });
    setEditingStaff(null);
    setShowForm(false);
    setError('');
    setShowPassword(false);
  };

  const openEditForm = (staff) => {
    setEditingStaff(staff.id);
    setFormData({
      name: staff.name,
      email: staff.email,
      password: '',
      permissions: { ...staff.permissions }
    });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and email are required');
      return;
    }
    if (!editingStaff && !formData.password.trim()) {
      setError('Password is required for new staff');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        permissions: formData.permissions
      };
      if (formData.password.trim()) {
        payload.password = formData.password;
      }

      if (editingStaff) {
        await axios.put(`${API}/api/admin/staff/${editingStaff}`, payload, { headers });
      } else {
        payload.password = formData.password;
        await axios.post(`${API}/api/admin/staff`, payload, { headers });
      }

      await fetchStaff();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete ${staffName}? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/api/admin/staff/${staffId}`, { headers });
      await fetchStaff();
      if (expandedStaff === staffId) setExpandedStaff(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete staff member');
    }
  };

  const handleToggleActive = async (staff) => {
    try {
      await axios.put(`${API}/api/admin/staff/${staff.id}`, { active: !staff.active }, { headers });
      await fetchStaff();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleGenerateApiKey = async (staffId) => {
    setApiKeyLoading(staffId);
    try {
      const res = await axios.post(`${API}/api/admin/staff/${staffId}/generate-api-key`, {}, { headers });
      // Reveal the newly generated key immediately
      setRevealedKeys(prev => ({ ...prev, [staffId]: true }));
      await fetchStaff();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setApiKeyLoading(null);
    }
  };

  const handleRevokeApiKey = async (staffId) => {
    if (!window.confirm('Revoke this API key? Any external tools using it will stop working immediately.')) return;
    setApiKeyLoading(staffId);
    try {
      await axios.post(`${API}/api/admin/staff/${staffId}/revoke-api-key`, {}, { headers });
      setRevealedKeys(prev => { const n = { ...prev }; delete n[staffId]; return n; });
      await fetchStaff();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setApiKeyLoading(null);
    }
  };

  const handleTogglePublishMode = async (staff) => {
    const newMode = staff.api_publish_mode === 'direct' ? 'draft' : 'direct';
    try {
      await axios.put(`${API}/api/admin/staff/${staff.id}`, { api_publish_mode: newMode }, { headers });
      await fetchStaff();
    } catch (err) {
      setError('Failed to update publish mode');
    }
  };

  const copyToClipboard = (text, staffId) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(staffId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 12) + '••••••••••••••••' + key.substring(key.length - 4);
  };

  const setAllPermissions = (level) => {
    const perms = {};
    PERMISSION_CATEGORIES.forEach(cat => { perms[cat.id] = level; });
    setFormData(prev => ({ ...prev, permissions: perms }));
  };

  const setGroupPermissions = (group, level) => {
    const perms = { ...formData.permissions };
    PERMISSION_CATEGORIES.filter(c => c.group === group).forEach(cat => { perms[cat.id] = level; });
    setFormData(prev => ({ ...prev, permissions: perms }));
  };

  const filteredStaff = staffList.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groups = [...new Set(PERMISSION_CATEGORIES.map(c => c.group))];

  const countPermissions = (permissions) => {
    if (!permissions) return { view: 0, edit: 0, full: 0 };
    return {
      view: Object.values(permissions).filter(v => v === 'view').length,
      edit: Object.values(permissions).filter(v => v === 'edit').length,
      full: Object.values(permissions).filter(v => v === 'full').length,
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="staff-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="h-7 w-7 text-purple-400" />
            Staff Management
          </h2>
          <p className="text-gray-400 mt-1">Manage staff users and their access permissions</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-purple-600 hover:bg-purple-500"
          data-testid="add-staff-btn"
        >
          <Plus className="h-4 w-4 mr-2" />Add Staff Member
        </Button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600/40 rounded-lg p-3 flex items-center gap-2 text-red-300">
          <AlertCircle className="h-4 w-4" />{error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300"><XCircle className="h-4 w-4" /></button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 space-y-6" data-testid="staff-form">
          <h3 className="text-lg font-bold text-white">
            {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="John Smith"
                  className="pl-10 bg-gray-800 border-gray-700 text-white"
                  data-testid="staff-name-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email (used to login)</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="staff@ltvproduction.com"
                  className="pl-10 bg-gray-800 border-gray-700 text-white"
                  data-testid="staff-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">
                {editingStaff ? 'New Password (leave blank to keep)' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                  placeholder="Strong password"
                  className="pl-10 pr-10 bg-gray-800 border-gray-700 text-white"
                  data-testid="staff-password-input"
                />
                <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-white">Permissions</h4>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs border-gray-700" onClick={() => setAllPermissions('none')}>All None</Button>
                <Button size="sm" variant="outline" className="text-xs border-blue-700 text-blue-400" onClick={() => setAllPermissions('view')}>All View</Button>
                <Button size="sm" variant="outline" className="text-xs border-amber-700 text-amber-400" onClick={() => setAllPermissions('edit')}>All Edit</Button>
                <Button size="sm" variant="outline" className="text-xs border-green-700 text-green-400" onClick={() => setAllPermissions('full')}>All Full</Button>
              </div>
            </div>

            <div className="space-y-6">
              {groups.map(group => (
                <div key={group}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-300">{group}</span>
                    <div className="flex gap-1">
                      {PERMISSION_LEVELS.map(level => (
                        <button key={level.value} onClick={() => setGroupPermissions(group, level.value)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 hover:text-gray-300">
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {PERMISSION_CATEGORIES.filter(c => c.group === group).map(cat => (
                      <div key={cat.id} className="flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-2.5">
                        <span className="text-sm text-gray-300">{cat.label}</span>
                        <PermissionSelector
                          value={formData.permissions[cat.id] || 'none'}
                          onChange={(val) => setFormData(p => ({
                            ...p,
                            permissions: { ...p.permissions, [cat.id]: val }
                          }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
            <Button variant="outline" onClick={resetForm} className="border-gray-700" data-testid="cancel-staff-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-500" data-testid="save-staff-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingStaff ? 'Update Staff' : 'Create Staff'}
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      {staffList.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search staff by name or email..."
            className="pl-10 bg-gray-800 border-gray-700 text-white"
            data-testid="search-staff-input"
          />
        </div>
      )}

      {/* Staff List */}
      {filteredStaff.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-xl border border-gray-800" data-testid="no-staff">
          <Users className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-bold text-white mb-2">
            {staffList.length === 0 ? 'No staff members yet' : 'No results found'}
          </h3>
          <p className="text-gray-400 mb-4">
            {staffList.length === 0 ? 'Add your first staff member to get started' : 'Try a different search term'}
          </p>
          {staffList.length === 0 && (
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-purple-600 hover:bg-purple-500">
              <Plus className="h-4 w-4 mr-2" />Add First Staff Member
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="staff-list">
          {filteredStaff.map(staff => {
            const permCounts = countPermissions(staff.permissions);
            const isExpanded = expandedStaff === staff.id;

            return (
              <div key={staff.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden" data-testid={`staff-card-${staff.id}`}>
                <div className="p-4 flex items-center gap-4">
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                    staff.active ? 'bg-purple-600' : 'bg-gray-600'
                  }`}>
                    {staff.name.charAt(0).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{staff.name}</span>
                      <Badge className={staff.active ? 'bg-green-600/20 text-green-400 border-green-600/30' : 'bg-red-600/20 text-red-400 border-red-600/30'}>
                        {staff.active ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-400 truncate">{staff.email}</p>
                  </div>

                  {/* Permission Summary */}
                  <div className="hidden md:flex items-center gap-2 text-xs">
                    {permCounts.full > 0 && <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded">{permCounts.full} Full</span>}
                    {permCounts.edit > 0 && <span className="bg-amber-900/30 text-amber-400 px-2 py-1 rounded">{permCounts.edit} Edit</span>}
                    {permCounts.view > 0 && <span className="bg-blue-900/30 text-blue-400 px-2 py-1 rounded">{permCounts.view} View</span>}
                    {permCounts.full === 0 && permCounts.edit === 0 && permCounts.view === 0 && (
                      <span className="bg-gray-800 text-gray-500 px-2 py-1 rounded">No permissions</span>
                    )}
                  </div>

                  {/* Last Login */}
                  <div className="hidden lg:block text-right">
                    <p className="text-xs text-gray-500">Last login</p>
                    <p className="text-sm text-gray-400">
                      {staff.last_login ? new Date(staff.last_login).toLocaleDateString() : 'Never'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setExpandedStaff(isExpanded ? null : staff.id)}
                      className="text-gray-400 hover:text-white" data-testid={`expand-staff-${staff.id}`}>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleToggleActive(staff)}
                      className={staff.active ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'}
                      data-testid={`toggle-active-${staff.id}`}>
                      {staff.active ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEditForm(staff)}
                      className="text-blue-400 hover:text-blue-300" data-testid={`edit-staff-${staff.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(staff.id, staff.name)}
                      className="text-red-400 hover:text-red-300" data-testid={`delete-staff-${staff.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Permissions + API Key */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t border-gray-800 space-y-4">
                    {/* API Key Management */}
                    {(staff.permissions?.blog === 'edit' || staff.permissions?.blog === 'full') && (
                      <div className="bg-gray-800/60 rounded-lg p-4 space-y-3" data-testid={`api-key-section-${staff.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Key className="h-4 w-4 text-amber-400" />
                            <span className="text-sm font-medium text-white">Blog API Key</span>
                          </div>
                          {staff.api_key ? (
                            <Badge className={staff.api_key_enabled
                              ? 'bg-green-600/20 text-green-400 border-green-600/30'
                              : 'bg-red-600/20 text-red-400 border-red-600/30'
                            }>
                              {staff.api_key_enabled ? 'Active' : 'Disabled'}
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-700/50 text-gray-400 border-gray-600/30">No Key</Badge>
                          )}
                        </div>

                        {staff.api_key ? (
                          <>
                            {/* Key display */}
                            <div className="flex items-center gap-2">
                              <code className="flex-1 bg-gray-900 text-gray-300 px-3 py-2 rounded text-xs font-mono truncate" data-testid={`api-key-display-${staff.id}`}>
                                {revealedKeys[staff.id] ? staff.api_key : maskApiKey(staff.api_key)}
                              </code>
                              <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white shrink-0"
                                onClick={() => setRevealedKeys(prev => ({ ...prev, [staff.id]: !prev[staff.id] }))}
                                data-testid={`toggle-reveal-key-${staff.id}`}>
                                {revealedKeys[staff.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                              </Button>
                              <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white shrink-0"
                                onClick={() => copyToClipboard(staff.api_key, staff.id)}
                                data-testid={`copy-api-key-${staff.id}`}>
                                {copiedKey === staff.id ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                              </Button>
                            </div>

                            {/* Publish mode toggle */}
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-gray-400">Publish Mode</p>
                                <p className="text-sm text-gray-200">
                                  {staff.api_publish_mode === 'direct' ? 'Direct Publish' : 'Save as Draft'}
                                </p>
                              </div>
                              <Button size="sm" variant="outline"
                                className={`text-xs border-gray-700 ${staff.api_publish_mode === 'direct' ? 'text-green-400' : 'text-amber-400'}`}
                                onClick={() => handleTogglePublishMode(staff)}
                                data-testid={`toggle-publish-mode-${staff.id}`}>
                                Switch to {staff.api_publish_mode === 'direct' ? 'Draft' : 'Direct'}
                              </Button>
                            </div>

                            {/* Key actions */}
                            <div className="flex gap-2 pt-1">
                              <Button size="sm" variant="outline" className="text-xs border-gray-700 text-amber-400 hover:text-amber-300"
                                onClick={() => handleGenerateApiKey(staff.id)}
                                disabled={apiKeyLoading === staff.id}
                                data-testid={`regenerate-api-key-${staff.id}`}>
                                {apiKeyLoading === staff.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                                Regenerate Key
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs border-red-800 text-red-400 hover:text-red-300"
                                onClick={() => handleRevokeApiKey(staff.id)}
                                disabled={apiKeyLoading === staff.id}
                                data-testid={`revoke-api-key-${staff.id}`}>
                                Revoke Key
                              </Button>
                            </div>

                            {/* Usage example */}
                            <details className="pt-1">
                              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300">Usage Example</summary>
                              <pre className="mt-2 bg-gray-900 rounded p-3 text-[11px] text-gray-400 overflow-x-auto whitespace-pre">
{`curl -X POST ${API}/api/blog/publish \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${revealedKeys[staff.id] ? staff.api_key : '<your-api-key>'}" \\
  -d '{
    "title": "My Article",
    "slug": "my-article",
    "excerpt": "Brief description",
    "category": "General",
    "content": "# Article content in markdown"
  }'`}
                              </pre>
                            </details>
                          </>
                        ) : (
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white"
                            onClick={() => handleGenerateApiKey(staff.id)}
                            disabled={apiKeyLoading === staff.id}
                            data-testid={`generate-api-key-${staff.id}`}>
                            {apiKeyLoading === staff.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Key className="h-3.5 w-3.5 mr-1.5" />}
                            Generate API Key
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Permission Details */}
                    <div>
                      <p className="text-sm font-medium text-gray-300 mb-3">Permission Details</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {PERMISSION_CATEGORIES.map(cat => {
                          const level = staff.permissions?.[cat.id] || 'none';
                          if (level === 'none') return null;
                          return (
                            <div key={cat.id} className="flex items-center justify-between bg-gray-800/50 rounded px-3 py-2">
                              <span className="text-xs text-gray-300">{cat.label}</span>
                              <PermissionBadge level={level} />
                            </div>
                          );
                        })}
                        {Object.values(staff.permissions || {}).every(v => v === 'none') && (
                          <p className="text-sm text-gray-500 col-span-full">No permissions assigned</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
